import {
  Box,
  Card,
  CardContent,
  CircularProgress,
  Stack,
  Typography,
} from '@mui/material';
import {
  SankeyChartProps,
  Unstable_SankeyChart as SankeyChart,
} from '@mui/x-charts-pro';

export type IncSankeyChartHeaders = {
  start?: string;
  center?: string;
  end?: string;
};

export type IncSankeyChartProps = {
  series?: SankeyChartProps['series'] | null;
  title?: string;
  headers?: IncSankeyChartHeaders;
  height?: number;
  loading?: boolean;
  emptyMessage?: string;
  SankeyChartProps?: Partial<SankeyChartProps>;
  'data-testid'?: string;
};

const IncSankeyChart = ({
  series,
  title,
  headers,
  height = 500,
  loading = false,
  emptyMessage,
  SankeyChartProps: sankeyChartProps = {},
  'data-testid': dataTestId = 'IncSankeyChart',
}: IncSankeyChartProps) => {
  // MUI X SankeyChart crashes on empty nodes/links (indexes into nodes without
  // guarding), so render an inline placeholder when there's no data.
  const seriesData = (
    series as
      | { data?: { nodes?: unknown[]; links?: unknown[] } }
      | null
      | undefined
  )?.data;
  const hasData = !!seriesData?.nodes?.length && !!seriesData?.links?.length;

  return (
    <Card data-testid={dataTestId}>
      <CardContent
        sx={{
          height: '100%',
          display: 'flex',
          flexDirection: 'column',
          gap: 1,
          '&:last-child': { paddingBottom: 2 },
        }}
      >
        {title && (
          <Typography variant="body2" component="div" margin={0}>
            {title}
          </Typography>
        )}
        {headers && (
          <Stack
            direction="row"
            justifyContent="space-between"
            data-testid={`${dataTestId}.Headers`}
          >
            <Typography variant="overline" color="text.secondary">
              {headers.start}
            </Typography>
            {headers.center && (
              <Typography
                variant="h3"
                data-testid={`${dataTestId}.CenterLabel`}
              >
                {headers.center}
              </Typography>
            )}
            <Typography variant="overline" color="text.secondary">
              {headers.end}
            </Typography>
          </Stack>
        )}
        <Stack flexGrow={1} minHeight={height}>
          {hasData ? (
            <SankeyChart
              series={series!}
              margin={{ top: 8, bottom: 8 }}
              loading={loading}
              data-testid={`${dataTestId}.Chart`}
              {...sankeyChartProps}
            />
          ) : (
            <Box
              flexGrow={1}
              minHeight={height}
              display="flex"
              alignItems="center"
              justifyContent="center"
              data-testid={`${dataTestId}.Empty`}
            >
              {loading ? (
                <CircularProgress />
              ) : (
                emptyMessage && (
                  <Typography variant="body2" color="text.secondary">
                    {emptyMessage}
                  </Typography>
                )
              )}
            </Box>
          )}
        </Stack>
      </CardContent>
    </Card>
  );
};

export default IncSankeyChart;
